import { Link, useLocation } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import {
  ChevronDown,
  Menu,
  X,
  Radar,
  Layers,
  Cpu,
  PieChart,
  Boxes,
  MessageSquare,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const primaryNav = [
  { label: "Home", href: "/" },
  { label: "Centre of Excellence", href: "/centre-of-excellence" },
  { label: "Capabilities", href: "/capabilities" },
  { label: "Technology Stack", href: "/technology-stack" },
  {
    label: "Industries",
    items: [
      { label: "Government", href: "/industries/government" },
      { label: "BFSI", href: "/industries/bfsi" },
      { label: "Digital-Native", href: "/industries/digital-native" },
      { label: "Defence", href: "/industries/defence" },
    ],
  },
  { label: "ROI & TCO", href: "/roi" },
  { label: "Architecture", href: "/architecture" },
  { label: "Engagement Models", href: "/engagement-models" },
  { label: "Insights", href: "/insights" },
  { label: "Contact", href: "/contact" },
];

function isGroup(item) {
  return item.items !== undefined;
}

function normalizePath(path) {
  if (path === "/") return path;
  return path.replace(/\/$/, "");
}

export function SiteHeader() {
  const location = useLocation();
  const activePath = normalizePath(location.pathname || "/");

  const [mobileOpen, setMobileOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const { user } = useAuth();

  const navWithAuth = useMemo(() => [
    ...primaryNav,
    { label: user ? "Dashboard" : "Login", href: user ? "/dashboard" : "/login" }
  ], [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (industriesOpen) {
        setIndustriesOpen(false);
      }
    };
    
    if (industriesOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [industriesOpen]);

  const industries = useMemo(() => {
    const group = primaryNav.find((i) => isGroup(i) && i.label === "Industries");
    return group && isGroup(group) ? group.items : [];
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8 pt-4">
        <div className="glass-panel rounded-2xl">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5 lg:px-6">
            <Link
              to="/"
              className="group flex items-center gap-2.5 rounded-xl px-1.5 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] shrink-0"
              aria-label="AIOps & Observability Centre of Excellence"
            >
              <div className="relative grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5">
                <Radar className="h-5 w-5 text-[var(--accent-primary)]" />
                <span className="pointer-events-none absolute inset-0 rounded-xl" style={{ boxShadow: "0 0 18px rgba(0,212,255,0.25)" }} />
              </div>
              <div className="leading-tight">
                <div className="font-[var(--font-display)] text-sm tracking-wide text-white">
                  AIOps & Observability
                </div>
                <div className="text-xs text-[var(--text-secondary)]">Centre of Excellence</div>
              </div>
            </Link>

            <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
              {navWithAuth.map((item) => {
                if (isGroup(item)) {
                  return (
                    <div key={item.label} className="relative">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIndustriesOpen((v) => !v);
                        }}
                        className="flex items-center gap-1 rounded-xl px-3 py-2 text-sm text-[var(--text-secondary)] hover:text-white hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)]"
                        aria-haspopup="menu"
                        aria-expanded={industriesOpen}
                      >
                        <span>{item.label}</span>
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      {industriesOpen ? (
                        <div
                          role="menu"
                          onMouseDown={(e) => e.preventDefault()}
                          className="absolute left-0 top-full mt-2 w-60 rounded-2xl border border-white/10 bg-[#0b1220]/95 backdrop-blur-xl shadow-[0_24px_60px_rgba(0,0,0,0.55)] z-50"
                        >
                          <div className="p-2">
                            {industries.map((sub) => {
                              const isActive = normalizePath(sub.href) === activePath;
                              return (
                                <Link
                                  key={sub.href}
                                  to={sub.href}
                                  role="menuitem"
                                  onClick={() => setIndustriesOpen(false)}
                                  className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                                    isActive
                                      ? "bg-white/8 text-white"
                                      : "text-[var(--text-secondary)] hover:text-white hover:bg-white/5"
                                  }`}
                                >
                                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)] opacity-70" />
                                  <span>{sub.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                }

                const isActive = normalizePath(item.href) === activePath;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`rounded-xl px-3 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] ${
                      isActive
                        ? "text-white bg-white/5"
                        : "text-[var(--text-secondary)] hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-2 shrink-0">
              

              <button
                type="button"
                className="inline-flex lg:hidden items-center justify-center rounded-xl p-2 text-[var(--text-secondary)] hover:text-white hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)]"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {mobileOpen ? (
            <div className="border-t border-white/8 px-4 py-4 lg:hidden">
              <div className="grid gap-2">
                <MobileLink href="/" label="Home" icon={<Radar className="h-4 w-4" />} onNavigate={() => setMobileOpen(false)} />
                <MobileLink
                  href="/centre-of-excellence"
                  label="Centre of Excellence"
                  icon={<Layers className="h-4 w-4" />}
                  onNavigate={() => setMobileOpen(false)}
                />
                <MobileLink
                  href="/capabilities"
                  label="Capabilities"
                  icon={<Cpu className="h-4 w-4" />}
                  onNavigate={() => setMobileOpen(false)}
                />
                <MobileLink
                  href="/technology-stack"
                  label="Technology Stack"
                  icon={<Boxes className="h-4 w-4" />}
                  onNavigate={() => setMobileOpen(false)}
                />

                <div className="mt-2 rounded-2xl border border-white/10 bg-white/3">
                  <div className="px-3 py-2 text-xs uppercase tracking-widest text-[var(--text-secondary)]">
                    Industries
                  </div>
                  <div className="grid gap-1 p-2">
                    {industries.map((sub) => (
                      <MobileLink
                        key={sub.href}
                        href={sub.href}
                        label={sub.label}
                        icon={<span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)]" />}
                        onNavigate={() => setMobileOpen(false)}
                      />
                    ))}
                  </div>
                </div>

                <MobileLink
                  href="/roi"
                  label="ROI & TCO"
                  icon={<PieChart className="h-4 w-4" />}
                  onNavigate={() => setMobileOpen(false)}
                />
                <MobileLink
                  href="/contact"
                  label="Contact"
                  icon={<MessageSquare className="h-4 w-4" />}
                  onNavigate={() => setMobileOpen(false)}
                />

              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function MobileLink({ href, label, icon, onNavigate }) {
  return (
    <Link
      to={href}
      onClick={onNavigate}
      className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-[var(--text-secondary)] hover:text-white hover:bg-white/5"
    >
      <span className="grid h-7 w-7 place-items-center rounded-lg border border-white/10 bg-white/5 text-[var(--accent-primary)]">
        {icon}
      </span>
      <span>{label}</span>
    </Link>
  );
}
